import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, ButtonGroup, Divider, Message, Panel, Stack, Tooltip, Whisper } from 'rsuite';

import { useProjects } from '../hooks/useQueries';

import './HomePage.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return currencyFormatter.format(n);
};

const plural = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;

const formatMonthsAsYearsAndMonths = (totalMonths) => {
  const monthsRaw = Number(totalMonths);
  if (!Number.isFinite(monthsRaw)) return '—';

  const monthsInt = Math.max(0, Math.floor(monthsRaw));
  const years = Math.floor(monthsInt / 12);
  const months = monthsInt % 12;

  if (years > 0) {
    return months > 0 ? `${plural(years, 'year')} ${plural(months, 'month')}` : plural(years, 'year');
  }
  return plural(months, 'month');
};

const parseDateParts = (iso) => {
  const s = typeof iso === 'string' ? iso.slice(0, 10) : '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return { year, month, day };
};

const dayNumber = (parts) => {
  if (!parts) return null;
  return Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / 86400000);
};

const monthDiffRoundedDown = (fromParts, toParts) => {
  const fromMonth = fromParts.year * 12 + (fromParts.month - 1);
  const toMonth = toParts.year * 12 + (toParts.month - 1);
  let months = toMonth - fromMonth;
  if (toParts.day < fromParts.day) months -= 1;
  return months;
};

const formatRemainingTime = (startIso, endIso) => {
  const endParts = parseDateParts(endIso);
  if (!endParts) return 'No end date';

  const startParts = parseDateParts(startIso);
  const now = new Date();
  const todayParts = { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };

  const endDay = dayNumber(endParts);
  const todayDay = dayNumber(todayParts);
  if (endDay === null || todayDay === null) return '—';
  if (endDay < todayDay) return 'Ended';
  if (endDay === todayDay) return 'Ends today';

  if (startParts) {
    const startDay = dayNumber(startParts);
    if (startDay !== null && startDay > todayDay) {
      const monthsUntilStart = monthDiffRoundedDown(todayParts, startParts);
      if (monthsUntilStart >= 1) return `Starts in ${formatMonthsAsYearsAndMonths(monthsUntilStart)}`;
      return `Starts in ${plural(Math.max(1, startDay - todayDay), 'day')}`;
    }
  }

  const monthsRemaining = monthDiffRoundedDown(todayParts, endParts);
  if (monthsRemaining >= 1) return `${formatMonthsAsYearsAndMonths(monthsRemaining)} remaining`;
  return `${plural(Math.max(1, endDay - todayDay), 'day')} remaining`;
};

const formatMonthYear = (iso) => {
  const s = typeof iso === 'string' ? iso.slice(0, 10) : null;
  if (!s) return null;
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(s);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
};

const initialsForName = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const getProjectTitleSizeClass = (name) => {
  const len = String(name || '').trim().length;
  if (len >= 64) return 'ppc-project-card-title--xxs';
  if (len >= 44) return 'ppc-project-card-title--xs';
  if (len >= 30) return 'ppc-project-card-title--sm';
  return '';
};

const AVATAR_PALETTE = [
  { background: '#f8d7da', color: '#7f1d2d' },
  { background: '#fde2b8', color: '#7a3a00' },
  { background: '#fff3bf', color: '#775d00' },
  { background: '#daf5d2', color: '#1f5d2f' },
  { background: '#d9f2f0', color: '#145a57' },
  { background: '#dbeafe', color: '#1e3a8a' },
  { background: '#e9ddff', color: '#4b2f77' },
  { background: '#f7d9ef', color: '#7c275e' },
  { background: '#fce1f1', color: '#7f1d4d' },
  { background: '#e5e7eb', color: '#374151' },
];

const hashText = (value) => {
  const s = String(value || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return hash;
};

const avatarStyleForName = (name) => {
  const paletteItem = AVATAR_PALETTE[hashText(name) % AVATAR_PALETTE.length];
  return {
    background: paletteItem.background,
    color: paletteItem.color,
    border: '1px solid rgba(0, 0, 0, 0.08)',
  };
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Back-compat: old deep links were "/?projectId=...&taskOrderId=...".
  useEffect(() => {
    const rawProjectId = searchParams?.get?.('projectId');
    const rawTaskOrderId = searchParams?.get?.('taskOrderId');
    const pid = rawProjectId ? Number(rawProjectId) : null;
    const toid = rawTaskOrderId ? Number(rawTaskOrderId) : null;

    if (!Number.isInteger(pid) || pid <= 0) return;

    if (Number.isInteger(toid) && toid > 0) {
      navigate(`/projects/${pid}/task-orders/${toid}`, { replace: true });
      return;
    }

    navigate(`/projects/${pid}`, { replace: true });
  }, [searchParams, navigate]);

  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  const openProject = (pid) => {
    if (!Number.isInteger(pid) || pid <= 0) return;
    navigate(`/projects/${pid}`);
  };

  return (
    <div className="ppc-page">
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <h3 style={{ margin: 0 }}>Projects</h3>
      </Stack>

      <Divider />

      <Panel
        bordered
        header={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <ButtonGroup>
              <Button
                size="xs"
                onClick={() => navigate('/projects/new')}
                aria-label="New Project"
                title="New Project"
                style={{ padding: '4px 10px' }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  New
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </span>
              </Button>
            </ButtonGroup>
          </div>
        }
      >
        {projectsLoading ? <div>Loading projects...</div> : null}
        {projectsError ? <Message type="error" showIcon>Failed to load projects.</Message> : null}

        <div className="ppc-home-project-grid" role="list" aria-label="Projects">
          {(projects || []).map((p) => {
            const pid = Number(p?.id);
            const assignedPeopleCount = Number(p?.assignedPeopleCount ?? p?.assigned_people_count ?? 0);
            const startDate = p?.startDate ?? p?.start_date;
            const endDate = p?.endDate ?? p?.end_date;
            const budgetTarget = p?.budgetTarget ?? p?.budget_target;
            const assignedPeopleNames = Array.isArray(p?.assignedPeopleNames) ? p.assignedPeopleNames : [];
            const rangeStart = formatMonthYear(startDate);
            const rangeEnd = formatMonthYear(endDate);
            const dateRange = rangeStart && rangeEnd ? `${rangeStart} - ${rangeEnd}` : '—';
            const remainingTime = formatRemainingTime(startDate, endDate);
            const visiblePeople = assignedPeopleNames.slice(0, 6);
            const overflowPeople = Math.max(0, assignedPeopleNames.length - visiblePeople.length);
            const hasLogo = Boolean(p?.photoUrl);
            const titleSizeClass = getProjectTitleSizeClass(p?.name);

            return (
              <Panel
                key={String(pid)}
                bordered
                className="ppc-home-project-card"
                role="listitem"
                tabIndex={0}
                onClick={() => openProject(pid)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') openProject(pid);
                }}
              >
                <span className="ppc-home-project-card__view-hint" aria-hidden="true" title="View details">
                  <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                </span>

                <div className="ppc-project-card-layout">
                  <div className={`ppc-project-card-top-row${hasLogo ? '' : ' ppc-project-card-top-row--no-logo'}`}>
                    <div className={`ppc-project-card-title${titleSizeClass ? ` ${titleSizeClass}` : ''}`} title={String(p?.name ?? '')}>
                      {p?.name || 'Untitled Project'}
                    </div>

                    {hasLogo ? (
                      <div className="ppc-project-card-logo-wrap" aria-label="Project logo">
                        <img src={p.photoUrl} alt="Project logo" className="ppc-project-card-logo" />
                      </div>
                    ) : null}
                  </div>

                  <div className="ppc-project-card-range" title={dateRange}>{dateRange}</div>

                  <div className="ppc-project-card-budget">Budget: {formatCurrency(budgetTarget)}</div>

                  <div className="ppc-project-card-people-section">
                    <div className="ppc-home-project-card__label">People</div>
                    <div className="ppc-project-card-people-row">
                      {visiblePeople.length > 0 ? (
                        visiblePeople.map((name, idx) => (
                          <Whisper key={`${name}-${idx}`} placement="top" trigger="hover" speaker={<Tooltip>{name}</Tooltip>}>
                            <div className="ppc-home-avatar" aria-label={name} style={avatarStyleForName(name)}>
                              {initialsForName(name)}
                            </div>
                          </Whisper>
                        ))
                      ) : (
                        <span className="ppc-project-card-people-empty">No assigned people</span>
                      )}
                      {overflowPeople > 0 ? (
                        <div className="ppc-home-avatar" title={`${overflowPeople} more`} aria-label={`${overflowPeople} more`}>
                          +{overflowPeople}
                        </div>
                      ) : null}
                    </div>
                    {Number.isFinite(assignedPeopleCount) && assignedPeopleCount > 0 ? (
                      <div className="ppc-project-card-people-count">{assignedPeopleCount} total</div>
                    ) : null}
                  </div>

                  <div className="ppc-project-card-footer">
                    <span className="ppc-project-card-remaining">{remainingTime}</span>
                  </div>
                </div>
              </Panel>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
